import { useCallback, useRef, useState, type DragEvent, type ChangeEvent } from "react";
import { UploadCloud } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/cn";

import { MAX_FILE_SIZE_MB } from "./constants";

interface Props {
  /** 파일이 선택/드롭됐을 때 호출 — 호출자가 검증·dedupe 책임. */
  onAdd: (files: File[]) => void;
  disabled?: boolean;
}

export default function UploadDropzone({ onAdd, disabled }: Props) {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;
      onAdd(Array.from(files));
    },
    [onAdd],
  );

  function handleDrop(e: DragEvent<HTMLLabelElement>): void {
    e.preventDefault();
    setIsDragging(false);
    if (disabled) return;
    handleFiles(e.dataTransfer.files);
  }

  function handleDragOver(e: DragEvent<HTMLLabelElement>): void {
    e.preventDefault();
    if (!disabled) setIsDragging(true);
  }

  function handleDragLeave(e: DragEvent<HTMLLabelElement>): void {
    e.preventDefault();
    setIsDragging(false);
  }

  function handleInputChange(e: ChangeEvent<HTMLInputElement>): void {
    handleFiles(e.target.files);
    // 같은 파일을 다시 선택해도 onChange 가 호출되도록 reset
    e.target.value = "";
  }

  return (
    <label
      htmlFor="upload-input"
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      className={cn(
        "flex flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-10 text-center transition",
        "cursor-pointer focus-within:ring-2 focus-within:ring-blue-400",
        isDragging
          ? "border-blue-400 bg-blue-50"
          : "border-slate-300 bg-slate-50 hover:border-slate-400 hover:bg-slate-100",
        disabled && "pointer-events-none cursor-not-allowed opacity-60",
      )}
    >
      <UploadCloud
        className={cn("h-10 w-10 transition", isDragging ? "text-blue-500" : "text-slate-400")}
      />
      <p className="mt-3 text-sm font-medium text-slate-700">{t("upload.dropzone.primary")}</p>
      <p className="mt-1 text-xs text-slate-400">
        {t("upload.dropzone.hint", { mb: MAX_FILE_SIZE_MB })}
      </p>

      <input
        ref={inputRef}
        id="upload-input"
        type="file"
        accept="application/pdf,.pdf,application/zip,.zip"
        multiple
        onChange={handleInputChange}
        disabled={disabled}
        className="sr-only"
      />
    </label>
  );
}
